import type { PdsModification, PdsOrderItem } from './types';

// Ce que les modifications de la commande ont fait à UN produit : de combien à combien, pourquoi, et ce qui a été remboursé.
export interface PdsItemChange {
  from: number;
  to: number;
  at: number;
  reason?: string;
  refundCents: number;
}

/*
 * L'historique du serveur nomme les produits par leur désignation, pas par l'identifiant de la ligne : on rattache
 * donc par le nom. Deux lignes qui portent le même nom (deux variantes) ne se distinguent pas, et rattacher la
 * modification à l'une plutôt qu'à l'autre serait deviner : ces lignes-là n'affichent rien.
 *
 * Plusieurs modifications du même produit (3 → 2 puis 2 → 0) se lisent comme une seule : de la première quantité à la
 * dernière, les montants additionnés, le motif de la plus récente. `modifications` arrive de la plus récente à la plus
 * ancienne.
 */
export const itemChanges = (
  items: PdsOrderItem[],
  modifications: PdsModification[],
) => {
  const changes = new Map<string, PdsItemChange>();
  const sameName = new Map<string, number>();
  items.forEach((item) =>
    sameName.set(item.name, (sameName.get(item.name) ?? 0) + 1),
  );
  const oldestFirst = [...modifications].reverse();

  for (const item of items) {
    if (sameName.get(item.name) !== 1) continue;
    const touched = oldestFirst.flatMap((modification) =>
      modification.lines
        .filter((line) => line.name === item.name)
        .map((line) => ({ line, modification })),
    );
    if (!touched.length) continue;
    const first = touched[0];
    const last = touched[touched.length - 1];
    if (first.line.from === last.line.to) continue;
    changes.set(item.id, {
      from: first.line.from,
      to: last.line.to,
      at: last.modification.at,
      reason: last.modification.reason,
      refundCents: touched.reduce((sum, t) => sum + t.line.refundCents, 0),
    });
  }
  return changes;
};
